import { channelTypeInfo } from "../../../src/models/ChannelType";
import { getOpeningEloProfile } from "../data/C1_OpeningEloDefinitions";
import { furnitureById } from "../furniture/C1_furnitureRegistry";
import {
  getEloItemProfile,
  getEloItemProfileForInstance,
  getRoomEloItemProfile,
} from "../data/C1_eloItemsRegistry";
import type { C1_Project } from "../model/C1_Project";
import type { C1_Opening } from "../model/C1_Opening";
import { C1_DOOR_TYPE_LABELS, C1_GARAGE_DOOR_TYPE_LABELS } from "../data/C1_doorTypes";
import type { C1_Furniture } from "../model/C1_Furniture";
import type { C1_EloItem_Definition, C1_EloItem_Data } from "../types/C1_EloTypes";
import { C1_EloItem_Type } from "../types/C1_EloTypes";
import { isLightGroupDefinitionId } from "../furniture/C1_lightGroup";
import { frozenTagAllocator } from "./C1_frozenTags";

let frozen: ReturnType<typeof frozenTagAllocator> | undefined;

export const C1_ELO_TAG_PREFIX: Partial<Record<C1_EloItem_Type, string>> = {
  [C1_EloItem_Type.Button]: "T",
  [C1_EloItem_Type.WindowContact]: "I",
  [C1_EloItem_Type.Light]: "L",
  [C1_EloItem_Type.Plug]: "P",
  [C1_EloItem_Type.PlugGroup]: "P",
  [C1_EloItem_Type.FixedPower]: "E",
  [C1_EloItem_Type.IntermediateDistribution]: "V",
  [C1_EloItem_Type.Blind]: "J",
  [C1_EloItem_Type.Jalousie]: "J",
  [C1_EloItem_Type.Heating]: "H",
};

type TagPrefix = "T" | "I" | "L" | "P" | "E" | "J" | "H" | "S" | "V";
type Counters = Record<TagPrefix, number>;
type TagAssignment = {
  instance: C1_EloItem_Data;
  type?: C1_EloItem_Type;
  /** Präfix und Zahl ohne fachlichen oder wiederholungsbedingten Suffix. */
  rootTag: string;
  /** Vollständiger Suffix in Hierarchiereihenfolge, beispielsweise `LA`. */
  suffix: string;
};

/** Writes one generated tag directly to its persistent ESTADA ELO object. */
function setGeneratedEloTag(item: C1_EloItem_Data, tag: string | undefined): void {
  const owner = state.objectRegistry.getObject(item.id);
  if (owner?.c1) owner.tag = (frozen ? frozen.eloTag(item, tag) : tag) ?? "";
}

function newCounters(): Counters {
  return { T: 0, I: 0, L: 0, P: 0, E: 0, J: 0, H: 0, S: 0, V: 0 };
}

function nextNumber(counters: Counters, prefix: TagPrefix): number {
  counters[prefix] += 1;
  return counters[prefix];
}

function numberedTag(prefix: TagPrefix, number: number, tagSuffix = ""): string {
  return `${prefix}${String(number).padStart(2, "0")}${tagSuffix}`;
}

function sortableText(value: unknown): string {
  return String(value ?? "").trim();
}

function uniqueNameTags(names: Array<string | undefined>): string[] {
  const used = new Map<string, number>();
  return names.map((name) => {
    const base = Array.from(sortableText(name))[0]?.toLocaleUpperCase("de") ?? "X";
    const duplicateIndex = used.get(base) ?? 0;
    used.set(base, duplicateIndex + 1);
    return duplicateIndex === 0 ? base : `${base}${duplicateIndex}`;
  });
}

function compareNames(left: unknown, right: unknown): number {
  return sortableText(left).localeCompare(sortableText(right), "de", {
    sensitivity: "base",
    numeric: true,
  });
}

function clearMasterIndices(project: C1_Project) {
  project.floors.forEach((floor) =>
    floor.rooms.forEach((room) => {
      room.setMasterIndex(undefined);
      room.openings.forEach((opening) => opening.setMasterIndex(undefined));
      room.furnitures.forEach((furniture) => furniture.setMasterIndex(undefined));
      for (const item of room.interactiveEloItems) item.setMasterIndex(undefined);
    }),
  );
}

function furnitureHasEloItems(furniture: C1_Furniture): boolean {
  const hasOwnEloItems = Boolean(
    furniture.eloTree &&
      (isLightGroupDefinitionId(furniture.definitionId) || getEloItemProfile(furniture.definitionId).length),
  );
  return hasOwnEloItems;
}

function assignMasterIndices(project: C1_Project, sortedFloors: C1_Project["floors"]) {
  let nextMasterIndex = 10;
  if (!frozen) clearMasterIndices(project);

  sortedFloors.forEach((floor) => {
    const sortedRooms = [...floor.rooms].sort((left, right) => {
      const leftRect = left.rect(project);
      const rightRect = right.rect(project);
      const leftCenter = { x: (leftRect.x1 + leftRect.x2) / 2, y: (leftRect.y1 + leftRect.y2) / 2 };
      const rightCenter = { x: (rightRect.x1 + rightRect.x2) / 2, y: (rightRect.y1 + rightRect.y2) / 2 };
      return leftCenter.y - rightCenter.y || leftCenter.x - rightCenter.x || compareNames(left.id, right.id);
    });

    sortedRooms.forEach((room) => {
      // Jeder Raum beginnt mit einem vollen Zehner. Benötigt der vorherige Raum
      // mehr als zehn Nummern, wird entsprechend auf den nächsten Zehner aufgerundet.
      nextMasterIndex = Math.ceil(nextMasterIndex / 10) * 10;
      if (frozen) frozen.index(room, true);
      else room.setMasterIndex(nextMasterIndex++);

      const roomRect = room.rect(project);
      const roomCenter = {
        x: (roomRect.x1 + roomRect.x2) / 2,
        y: (roomRect.y1 + roomRect.y2) / 2,
      };
      const openingAngle = (opening: C1_Opening) => {
        const offset = Math.min(1, Math.max(0, opening.offset ?? 0.5));
        const position =
          opening.side === "left"
            ? { x: roomRect.x1, y: roomRect.y1 + (roomRect.y2 - roomRect.y1) * offset }
            : opening.side === "right"
              ? { x: roomRect.x2, y: roomRect.y1 + (roomRect.y2 - roomRect.y1) * offset }
              : opening.side === "top"
                ? { x: roomRect.x1 + (roomRect.x2 - roomRect.x1) * offset, y: roomRect.y1 }
                : { x: roomRect.x1 + (roomRect.x2 - roomRect.x1) * offset, y: roomRect.y2 };
        const angle = Math.atan2(position.x - roomCenter.x, roomCenter.y - position.y);
        return (angle + Math.PI * 2) % (Math.PI * 2);
      };

      [...room.openings]
        .filter((opening) => Boolean(frozen) || Boolean(opening.eloTree))
        .sort((left, right) => openingAngle(left) - openingAngle(right) || compareNames(left.id, right.id))
        .forEach((opening) => frozen ? frozen.index(opening) : opening.setMasterIndex(nextMasterIndex++));

      room.furnitures
        .filter(furnitureHasEloItems)
        .sort(
          (left, right) =>
            left.relativePosition.y - right.relativePosition.y ||
            left.relativePosition.x - right.relativePosition.x ||
            compareNames(left.id, right.id),
        )
        .forEach((furniture) => frozen ? frozen.index(furniture) : furniture.setMasterIndex(nextMasterIndex++));
      // Direct room items share the room's master index, just like template instances.
    });
  });
}

function definitionForInstance(
  instance: C1_EloItem_Data,
  definitions: C1_EloItem_Definition[],
): C1_EloItem_Definition | undefined {
  const definitionId = instance.definitionId ?? "";
  const baseId = definitionId.replace(/-\d+$/, "");
  return definitions.find((definition) => definition.id === definitionId || definition.id === baseId);
}

function itemType(instance: C1_EloItem_Data, definition?: C1_EloItem_Definition): C1_EloItem_Type | undefined {
  if (definition?.type) return definition.type;
  if (instance.details?.plugType) return C1_EloItem_Type.Plug;
  if (instance.details?.channelType && channelTypeInfo(instance.details.channelType).isLight) return C1_EloItem_Type.Light;
  if (instance.details?.buttonType || instance.details?.numberOfButtonChannels) return C1_EloItem_Type.Button;
  if (instance.details?.voltage) return C1_EloItem_Type.FixedPower;
  if (instance.details?.sensorTypes) return C1_EloItem_Type.Sensor;
  return undefined;
}

function definitionName(instance: C1_EloItem_Data, definitions: C1_EloItem_Definition[]): string {
  return definitionForInstance(instance, definitions)?.description?.de ?? instance.definitionId ?? "";
}

function repeatIndex(instance: C1_EloItem_Data): number | undefined {
  const match = instance.definitionId.match(/-(\d+)$/);
  return match ? Number(match[1]) : undefined;
}

function isPlugOnly(definition: C1_EloItem_Definition | undefined): boolean {
  if (!definition) return false;
  if (definition.type === C1_EloItem_Type.Plug) return true;
  return Boolean(definition.items?.length) && definition.items!.every(isPlugOnly);
}

function letterSuffix(index: number): string {
  let value = index + 1;
  let result = "";
  while (value > 0) {
    value -= 1;
    result = String.fromCharCode(65 + (value % 26)) + result;
    value = Math.floor(value / 26);
  }
  return result;
}

function lowerCaseLetterSuffix(index: number): string {
  return letterSuffix(index).toLocaleLowerCase("de-DE");
}

function tagLightGroup(
  instance: C1_EloItem_Data | undefined,
  counters: Counters,
  tagByIndex: boolean,
  masterIndex?: number,
): void {
  if (!instance) return;
  const lights = instance.items.filter(
    (item) => item.definitionId.endsWith(".light"),
  );
  if (!lights.length) return;

  const number = tagByIndex && masterIndex !== undefined ? masterIndex : nextNumber(counters, "L");
  const baseTag = frozen?.existingEloTag(lights.find(light => frozen?.existingEloTag(light)) ?? lights[0]).match(/^L\d+/)?.[0] ?? numberedTag("L", number);
  lights.forEach((light, index) => {
    setGeneratedEloTag(light, index === 0 ? baseTag : `${baseTag}${lowerCaseLetterSuffix(index - 1)}`);
  });
}

function minimalUniqueSuffix(assignment: TagAssignment, siblings: TagAssignment[]): string {
  if (siblings.length <= 1) return "";

  // Von rechts beginnen, weil dort die konkrete Wiederholung (A/B/C) steht;
  // weiter links folgen allgemeinere fachliche Gruppenkennzeichen (D, L, R ...).
  // Es werden nur so viele Zeichen beibehalten, wie für Eindeutigkeit nötig sind.
  for (let length = 1; length <= assignment.suffix.length; length += 1) {
    const trailingCandidate = assignment.suffix.slice(-length);
    const trailingOccurrences = siblings.filter(
      (sibling) => sibling.suffix.slice(-length) === trailingCandidate,
    ).length;
    if (trailingOccurrences === 1) return trailingCandidate;

    // Wenn sich der konkrete Endsuffix nicht unterscheidet, kann der fachliche
    // Anfang bereits eindeutig sein: UB/SB wird U/S, LA/LB bleibt A/B.
    const leadingCandidate = assignment.suffix.slice(0, length);
    const leadingOccurrences = siblings.filter(
      (sibling) => sibling.suffix.slice(0, length) === leadingCandidate,
    ).length;
    if (leadingOccurrences === 1) return leadingCandidate;
  }

  // Identische vollständige Suffixe lassen sich durch Kürzen nicht eindeutig
  // machen. Der bisherige Wert bleibt erhalten, damit der Fall nicht schlechter wird.
  return assignment.suffix;
}

/**
 * Creates a tag per distinct room name. Multiple room rectangles can represent
 * parts of the same logical room, so equal names deliberately reuse one tag.
 */
function groupedRoomNameTags(names: Array<string | undefined>): string[] {
  const used = new Map<string, number>();
  const tagsByName = new Map<string, string>();

  return names.map((name) => {
    const normalizedName = sortableText(name).normalize("NFC").toLocaleLowerCase("de");
    const existingTag = tagsByName.get(normalizedName);
    if (existingTag !== undefined) return existingTag;

    const base = Array.from(sortableText(name))[0]?.toLocaleUpperCase("de") ?? "X";
    const duplicateIndex = used.get(base) ?? 0;
    used.set(base, duplicateIndex + 1);
    const tag = duplicateIndex === 0 ? base : `${base}${duplicateIndex}`;
    tagsByName.set(normalizedName, tag);
    return tag;
  });
}

function automaticTagSuffix(
  definition: C1_EloItem_Definition | undefined,
  siblingDefinitions: C1_EloItem_Definition[],
): string {
  if (!definition || definition.tagSuffix !== undefined) return definition?.tagSuffix ?? "";
  if (definition.hasTag === false) return "";
  if (definition.type === C1_EloItem_Type.IntermediateDistribution) return "";
  if (isPlugOnly(definition)) return "";
  if (definition.type === C1_EloItem_Type.Plug || definition.type === C1_EloItem_Type.PlugGroup) return "";

  const firstDescriptionLetter = (candidate: C1_EloItem_Definition) =>
    Array.from(candidate.description.de.trim())
      .find((character) => /\p{L}/u.test(character))
      ?.toLocaleUpperCase("de-DE") ?? "";
  const candidate = firstDescriptionLetter(definition);
  if (!candidate) return "";

  // Nur ein innerhalb derselben Definitionsliste eindeutiger Anfangsbuchstabe
  // eignet sich als automatisch erzeugtes Suffix. So werden beispielsweise
  // "Licht 1" und "Licht 2" nicht beide mit dem kollidierenden Suffix "L" getaggt.
  const occurrences = siblingDefinitions.filter(
    (siblingDefinition) => firstDescriptionLetter(siblingDefinition) === candidate,
  ).length;
  return occurrences === 1 ? candidate : "";
}

function tagEloInstances(
  instances: C1_EloItem_Data[],
  definitions: C1_EloItem_Definition[],
  counters: Counters,
  options: {
    tagByIndex: boolean;
    masterIndex?: number;
    tagSuffix?: string;
    plugBase?: string;
    basePlugBase?: string;
  },
  assignments: TagAssignment[],
): void {
  let plugChildIndex = 0;
  instances
    .map((instance, sourceIndex) => ({ instance, sourceIndex }))
    .sort(
      (left, right) =>
        compareNames(definitionName(left.instance, definitions), definitionName(right.instance, definitions)) ||
        (repeatIndex(left.instance) ?? left.sourceIndex + 1) - (repeatIndex(right.instance) ?? right.sourceIndex + 1) ||
        left.sourceIndex - right.sourceIndex,
    )
    .forEach(({ instance }) => {
      const definition = definitionForInstance(instance, definitions);
      const type = itemType(instance, definition);
      const definitionTagSuffix = automaticTagSuffix(definition, definitions);
      const itemTypePrefix = type ? C1_ELO_TAG_PREFIX[type] : undefined;
      // Das Typpräfix kennzeichnet atomare Items wie Taster (T), Leuchte (L)
      // oder Heizung (H) bereits. Derselbe Buchstabe wäre als eigener Suffix
      // redundant; fachliche Elternsuffixe wie L/R bleiben dagegen erhalten.
      const nonRedundantDefinitionSuffix = definitionTagSuffix === itemTypePrefix ? "" : definitionTagSuffix;
      const tagSuffix = options.tagByIndex ? `${options.tagSuffix ?? ""}${nonRedundantDefinitionSuffix}` : "";

      if (type === C1_EloItem_Type.Group || (instance.items?.length ?? 0) > 0) {
        if (isPlugOnly(definition)) {
          const number =
            options.tagByIndex && options.masterIndex !== undefined ? options.masterIndex : nextNumber(counters, "P");
          const existingChildTag = frozen && (instance.items ?? []).map(child => frozen!.existingEloTag(child)).find(tag => /^P\d+/.test(tag));
          const plugBase = frozen?.existingEloTag(instance) || existingChildTag?.match(/^P\d+/)?.[0] || numberedTag("P", number, tagSuffix);
          const basePlugBase = plugBase.match(/^P\d+/)?.[0] ?? numberedTag("P", number);
          if (definition?.hasTag === false) {
            setGeneratedEloTag(instance, undefined);
          } else {
            setGeneratedEloTag(instance, plugBase);
            assignments.push({
              instance,
              rootTag: basePlugBase,
              suffix: tagSuffix,
            });
          }
          tagEloInstances(
            instance.items ?? [],
            definition?.items ?? [],
            counters,
            {
              ...options,
              tagSuffix,
              plugBase,
              basePlugBase,
            },
            assignments,
          );
        } else {
          if (definition?.hasTag === false) {
            setGeneratedEloTag(instance, undefined);
          } else {
            const prefix: TagPrefix = "S";
            const number =
              options.tagByIndex && options.masterIndex !== undefined
                ? options.masterIndex
                : nextNumber(counters, prefix);
            const disambiguatedTag = numberedTag(prefix, number, tagSuffix);
            const baseTag = numberedTag(prefix, number);
            setGeneratedEloTag(instance, disambiguatedTag);
            assignments.push({
              instance,
              rootTag: baseTag,
              suffix: tagSuffix,
            });
          }
          tagEloInstances(
            instance.items ?? [],
            definition?.items ?? [],
            counters,
            {
              ...options,
              tagSuffix,
              plugBase: undefined,
              basePlugBase: undefined,
            },
            assignments,
          );
        }
        return;
      }

      if (definition?.hasTag === false) {
        setGeneratedEloTag(instance, undefined);
        return;
      }

      const prefix = (type && (C1_ELO_TAG_PREFIX[type] as TagPrefix | undefined)) ?? "S";
      if (prefix === "P" && options.plugBase) {
        const childSuffix = letterSuffix(plugChildIndex);
        const disambiguatedTag = `${options.plugBase}${childSuffix}`;
        setGeneratedEloTag(instance, disambiguatedTag);
        assignments.push({
          instance,
          rootTag: options.basePlugBase ?? options.plugBase,
          suffix: `${tagSuffix}${childSuffix}`,
        });
        plugChildIndex += 1;
        return;
      }

      const number =
        options.tagByIndex && options.masterIndex !== undefined ? options.masterIndex : nextNumber(counters, prefix);
      const instanceRepeatIndex = repeatIndex(instance);
      const repeatedBaseId = instance.definitionId.replace(/-\d+$/, "");
      const repeatedSiblingCount = instances.filter(
        (candidate) => candidate.definitionId.replace(/-\d+$/, "") === repeatedBaseId,
      ).length;
      const repeatedSuffix =
        instanceRepeatIndex !== undefined && repeatedSiblingCount > 1 ? letterSuffix(instanceRepeatIndex - 1) : "";
      const disambiguatedTag = numberedTag(prefix, number, `${tagSuffix}${repeatedSuffix}`);
      const baseTag = numberedTag(prefix, number);
      setGeneratedEloTag(instance, disambiguatedTag);
      assignments.push({
        instance,
        rootTag: baseTag,
        type,
        suffix: `${tagSuffix}${repeatedSuffix}`,
      });
  });
}

function tagFurnitureHierarchy(
  furniture: C1_Furniture,
  counters: Counters,
  tagByIndex: boolean,
  masterIndex: number | undefined,
  assignments: TagAssignment[],
): C1_EloItem_Data | undefined {
  const eloTree = furniture.eloTree;
  const eloDefinitions = getEloItemProfileForInstance(
    furniture.definitionId,
    eloTree,
  );
  if (isLightGroupDefinitionId(furniture.definitionId)) {
    tagLightGroup(eloTree, counters, tagByIndex, masterIndex);
  } else {
    tagEloInstances(
      eloTree?.items ?? [],
      eloDefinitions,
      counters,
      { tagByIndex, masterIndex },
      assignments,
    );
  }
  return eloTree;
}

/**
 * Generates stable human-readable tags. Arrays are not reordered; sorted copies
 * are used only to determine numbering.
 */
export function generateTags(project: C1_Project): void {
  const previous = frozen;
  frozen = areTagsFrozen(project) ? frozenTagAllocator(project) : undefined;
  try {
    generateProjectTags(project);
    if (frozen) project.setTagNumbering(frozen.ledger);
  } finally { frozen = previous; }
}

export function areTagsFrozen(project: C1_Project): boolean {
  return project.data?.tagNumbering?.frozen ?? Boolean(project.data?.tagNumbering || state.settings?.applicationMode === "main");
}

/** The caller owns the undo step; false discards reservations and recalculates every tag. */
export function setTagsFrozen(project: C1_Project, value: boolean): void {
  if (value === areTagsFrozen(project)) return;
  project.setTagNumbering(value
    ? { ...(project.data.tagNumbering ?? { masterIndexHighWater: 0, reservedTags: {} }), frozen: true }
    : { frozen: false, masterIndexHighWater: 0, reservedTags: {} });
  generateTags(project);
}

function generateProjectTags(project: C1_Project): void {
  const globalCounters = newCounters();
  for (const tag of frozen?.ledger.reservedTags.elo ?? []) {
    const match = tag.match(/^([TILPEJHSV])(\d+)/);
    if (match) globalCounters[match[1] as TagPrefix] = Math.max(globalCounters[match[1] as TagPrefix], Number(match[2]));
  }
  const tagAssignments: TagAssignment[] = [];
  const sortedFloors = [...project.floors].sort(
    (left, right) => left.level - right.level || compareNames(left.id, right.id),
  );
  if (project.settings.tagByIndex) assignMasterIndices(project, sortedFloors);
  else if (!frozen) clearMasterIndices(project);
  const floorTags = uniqueNameTags(sortedFloors.map((floor) => (floor.owner?.name ?? "")));

  sortedFloors.forEach((floor, floorIndex) => {
    floor.setTag(frozen ? frozen.structuralTag("floors", floor.tag, floorTags[floorIndex]) : floorTags[floorIndex]);

    const sortedRooms = [...floor.rooms].sort(
      (left, right) => compareNames((left.owner?.name ?? ""), (right.owner?.name ?? "")) || compareNames(left.id, right.id),
    );
    const roomTags = groupedRoomNameTags(sortedRooms.map((room) => (room.owner?.name ?? "")));

    sortedRooms.forEach((room, roomIndex) => {
      const proposedRoomTag = project.settings.tagByIndex ? String(room.masterIndex) : roomTags[roomIndex];
      const existingRoomTag = project.settings.tagByIndex && !/^\d+$/.test(room.tag) ? "" : room.tag;
      room.setTag(frozen ? frozen.structuralTag(`rooms:${floor.id}`, existingRoomTag, proposedRoomTag) : proposedRoomTag);
      const counters = globalCounters;

      const sources = [
        ...room.furnitures.map((furniture) => {
          const definition = furnitureById[furniture.definitionId];
          return {
            id: furniture.id,
            name: definition?.description?.de ?? furniture.definitionId ?? "",
            run: () => {
              tagFurnitureHierarchy(
                furniture,
                counters,
                project.settings.tagByIndex,
                furniture.masterIndex,
                tagAssignments,
              );
            },
          };
        }),
        ...room.openings.map((opening) => ({
          id: opening.id,
          name:
            opening.kind === "window"
              ? "Fenster"
              : opening.kind === "garage-door"
                ? C1_GARAGE_DOOR_TYPE_LABELS[opening.garageDoorType].de
                : C1_DOOR_TYPE_LABELS[opening.doorType].de,
          run: () => {
            const profile = getOpeningEloProfile(opening);
            const eloTree = opening.eloTree;
            tagEloInstances(
              eloTree?.items ?? [],
              profile.definitions,
              counters,
              {
                tagByIndex: project.settings.tagByIndex,
                masterIndex: opening.masterIndex,
              },
              tagAssignments,
            );
          },
        })),
      ].sort((left, right) => compareNames(left.name, right.name) || compareNames(left.id, right.id));

      sources.forEach((source) => source.run());
      const roomEloTree = room.eloTree;
      const roomDefinitions = new Map(getRoomEloItemProfile(room.type).map(definition => [definition.id, definition]));
      const roomInstances = (roomEloTree?.items ?? []).filter(item => !item.interactivCreated);
      for (const item of room.interactiveEloItems) {
        const definition = item.definition?.toDefinition();
        if (definition) roomDefinitions.set(definition.id, definition);
        roomInstances.push(item.toLegacyTreeData());
      }
      tagEloInstances(
        roomInstances,
        [...roomDefinitions.values()],
        counters,
        {
          tagByIndex: project.settings.tagByIndex,
          masterIndex: room.masterIndex,
        },
        tagAssignments,
      );
    });
  });

  if (project.settings.tagByIndex && !frozen) {
    const assignmentsByRootTag = new Map<string, TagAssignment[]>();
    tagAssignments.forEach((assignment) => {
      const siblings = assignmentsByRootTag.get(assignment.rootTag) ?? [];
      siblings.push(assignment);
      assignmentsByRootTag.set(assignment.rootTag, siblings);
    });

    const usedTags = new Set<string>();
    tagAssignments.forEach((assignment) => {
      const siblings = assignmentsByRootTag.get(assignment.rootTag) ?? [assignment];
      let suffix = minimalUniqueSuffix(assignment, siblings);
      // Identical instances (including manually placed ones) have no definition
      // postfix to distinguish them. Number those siblings with A/B/C instead.
      const collisions = siblings.filter(sibling => minimalUniqueSuffix(sibling, siblings) === suffix);
      if (collisions.length > 1) suffix += letterSuffix(collisions.indexOf(assignment));
      let tag = `${assignment.rootTag}${suffix}`;
      let extra = 0;
      while (usedTags.has(tag)) tag = `${assignment.rootTag}${suffix}${letterSuffix(extra++)}`;
      usedTags.add(tag);
      setGeneratedEloTag(
        assignment.instance,
        tag,
      );
    });
  }

  if (project.settings.tagByIndex) {
    // Distribution suffixes express the current number of distributions, not
    // their description or historical tag reservations: V20 or V20A/V20B.
    const distributionsByRoot = new Map<string, TagAssignment[]>();
    for (const assignment of tagAssignments) {
      if (assignment.type !== C1_EloItem_Type.IntermediateDistribution) continue;
      const siblings = distributionsByRoot.get(assignment.rootTag) ?? [];
      siblings.push(assignment);
      distributionsByRoot.set(assignment.rootTag, siblings);
    }
    for (const [root, siblings] of distributionsByRoot) {
      siblings.forEach((assignment, index) => {
        const owner = state.objectRegistry.getObject(assignment.instance.id);
        const tag = root + (siblings.length > 1 ? letterSuffix(index) : "");
        if (owner?.c1) owner.tag = tag;
        if (frozen) {
          const reserved = frozen.ledger.reservedTags.elo ??= [];
          if (!reserved.includes(tag)) reserved.push(tag);
        }
      });
    }
  }
}
